import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Pencil, Trash2 } from "lucide-react";

import { getNoticeDetail, updateNotice, deleteNotice } from "../../api/meeting";

import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import TinyMCEpreview from "../../components/const/TinyMCEpreview";
import { softDeleteDoc } from "../../api/papers";

export default function NoticeView() {
  const { id, noticeId } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);

  const [item, setItem] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [title, setTitle] = useState("");

  const fetchData = async () => {
    try {
      const res = await getNoticeDetail(noticeId);
      setItem(res.data);
      setTitle(res.data.title);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [noticeId]);

  const handleSave = async () => {
    const newContext = editorRef.current?.getContent() ?? "";
    try {
      await updateNotice(noticeId, {
        meta: { title, context: newContext },
      });
      alert("수정되었습니다.");
      setIsEdit(false);
      fetchData();
    } catch (e) {
      console.error(e);
      alert("수정 실패");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      await softDeleteDoc(noticeId, true);
      alert("삭제되었습니다.");
      navigate(`/project/${id}/meeting/notice`);
    } catch (e) {
      console.error(e);
    }
  };

  function formatKoreanDate(datetime) {
    if (!datetime) return "";
    const d = new Date(datetime);
    return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}`;
  }

  if (!item) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-[15px] text-slate-400">불러오는 중...</p>
      </div>
    );
  }

  const createdAtText = formatKoreanDate(item.created_at);

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-lg px-4 pt-6 pb-28">
        {/* 뒤로가기 */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-1 text-[15px] text-slate-500 active:opacity-70"
        >
          <ChevronLeft size={20} />
          목록으로
        </button>

        {/* 헤더 영역: 제목 + 메타정보 */}
        <header className="mb-6">
          {isEdit ? (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[20px] font-bold text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
              placeholder="제목을 입력하세요"
            />
          ) : (
            <h1 className="text-[24px] font-bold leading-snug tracking-tight text-slate-900">
              {item.title}
            </h1>
          )}

          <div className="mt-3 flex items-center gap-2 text-[14px] text-slate-500">
            <span className="font-medium text-slate-700">
              {item.created_by || "작성자 미상"}
            </span>
            <span className="text-slate-300">·</span>
            <span>{createdAtText}</span>
          </div>
        </header>

        {/* 내용 영역 */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="min-h-[200px] px-5 py-5 text-[15px] leading-relaxed text-slate-800">
            {isEdit ? (
              <TinyMCEeditor
                initialValue={item.meta?.context || ""}
                editorRef={editorRef}
              />
            ) : (
              <TinyMCEpreview html={item.meta?.context || ""} />
            )}
          </div>
        </section>
      </main>

      {/* 하단 액션 버튼 */}
      <footer className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur-sm px-4 py-3 safe-area-bottom">
        <div className="mx-auto max-w-lg flex items-center gap-3">
          {isEdit ? (
            <>
              <button
                onClick={() => {
                  setIsEdit(false);
                  setTitle(item.title);
                }}
                className="flex-1 h-12 rounded-xl border border-slate-200 bg-white text-[15px] font-semibold text-slate-700 active:scale-[0.98] transition-transform"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                className="flex-1 h-12 rounded-xl bg-slate-900 text-[15px] font-semibold text-white active:scale-[0.98] transition-transform"
              >
                저장
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleDelete}
                className="flex h-12 w-12 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-600 active:scale-[0.95] transition-transform"
                title="삭제"
              >
                <Trash2 size={20} />
              </button>
              <button
                onClick={() => setIsEdit(true)}
                className="flex-1 h-12 rounded-xl bg-slate-900 text-[15px] font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              >
                <Pencil size={16} />
                수정
              </button>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}
